import React, { useMemo, useState } from 'react'
import { api } from '../api'
import { calculateProductTotalCost, isSemiFinishedEligibleForRecipeReport, formatQuantity } from '../costUtils'
import {
  Category,
  ComponentItem,
  ComponentType,
  Packaging,
  Product,
  RawMaterial,
  SemiFinishedProduct,
  TradingGood,
} from '../types'
import { SearchableComponentSelect, SelectableComponentOption } from './SearchableComponentSelect'

interface Props {
  products: Product[]
  setProducts?: React.Dispatch<React.SetStateAction<Product[]>>
  setDemoProducts?: React.Dispatch<React.SetStateAction<any[]>>
  categories: Category[]
  rawMaterials: RawMaterial[]
  packagings: Packaging[]
  tradingGoods?: TradingGood[]
  semiFinished: SemiFinishedProduct[]
  onRefresh: () => Promise<void> | void
  onRefreshProducts?: () => Promise<void> | void
  onCascadeRefresh?: () => Promise<void> | void
  onOpenReport?: (item: any, type: string) => void
}

interface SingleCellEditState {
  product: Product
  field: string
  label: string
  value: any
}

function PieChartIcon({ size = 13, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px', ...style }}
    >
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  )
}

function ChevronIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function PencilIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function SettingsGearIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

export function ProductsView({
  products,
  setProducts,
  setDemoProducts,
  categories,
  rawMaterials,
  packagings,
  tradingGoods = [],
  semiFinished,
  onRefresh,
  onRefreshProducts,
  onCascadeRefresh,
  onOpenReport,
}: Props) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [activeProductForAddComponent, setActiveProductForAddComponent] = useState<Product | null>(null)
  const [editingCompQuantity, setEditingCompQuantity] = useState<{
    product: Product
    component: ComponentItem
    quantity: string
  } | null>(null)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [singleCellEdit, setSingleCellEdit] = useState<SingleCellEditState | null>(null)
  const [bulkEditField, setBulkEditField] = useState<{ field: string; label: string } | null>(null)
  const [gearMenu, setGearMenu] = useState<{ product: Product; x: number; y: number } | null>(null)

  const [localHiddenProductIds, setLocalHiddenProductIds] = useState<Set<string>>(new Set())
  const [localHiddenComponentIds, setLocalHiddenComponentIds] = useState<Set<string>>(new Set())

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Maps for fast recursive BOM tree lookup
  const semiFinishedMap = React.useMemo(() => {
    return new Map(semiFinished.map((s) => [s.id, s]))
  }, [semiFinished])

  const productsMap = React.useMemo(() => {
    return new Map(products.map((p) => [p.id, p]))
  }, [products])

  const rawMaterialsMap = React.useMemo(() => {
    return new Map(rawMaterials.map((r) => [r.id, r]))
  }, [rawMaterials])

  const packagingsMap = React.useMemo(() => {
    return new Map(packagings.map((p) => [p.id, p]))
  }, [packagings])

  const tradingGoodsMap = React.useMemo(() => {
    return new Map((tradingGoods || []).map((t) => [t.id, t]))
  }, [tradingGoods])

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  // New Product Form state
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [image, setImage] = useState('')
  const [unit, setUnit] = useState('Adet')
  const [barcode, setBarcode] = useState('')
  const [altBarcodes, setAltBarcodes] = useState('')
  const [kdvRate, setKdvRate] = useState('0.20')
  const [recipeOutputQuantity, setRecipeOutputQuantity] = useState('1')
  const [methodOverride, setMethodOverride] = useState('')

  // Edit Product Form state
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [editImage, setEditImage] = useState('')
  const [editUnit, setEditUnit] = useState('Adet')
  const [editBarcode, setEditBarcode] = useState('')
  const [editAltBarcodes, setEditAltBarcodes] = useState('')
  const [editKdvRate, setEditKdvRate] = useState('0.20')
  const [editRecipeOutputQuantity, setEditRecipeOutputQuantity] = useState('1')
  const [editMethodOverride, setEditMethodOverride] = useState('')

  // Component Modal Form: 2-step flow
  const [selectedCompType, setSelectedCompType] = useState<ComponentType>('raw_material')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [compQuantity, setCompQuantity] = useState('1.0000')
  const [compUnitMode, setCompUnitMode] = useState<'kg' | 'g'>('kg')
  const [compError, setCompError] = useState<string | null>(null)

  // Bulk edit values
  const [bulkUnit, setBulkUnit] = useState('Adet')
  const [bulkCategory, setBulkCategory] = useState('')
  const [bulkKdv, setBulkKdv] = useState('0.20')

  // Search and Category Filter
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (localHiddenProductIds.has(p.id)) return false
      const term = searchTerm.trim().toLowerCase()
      const matchSearch =
        !term ||
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.code && p.code.toLowerCase().includes(term))
      const matchCategory = !selectedCategory || p.category_id === selectedCategory
      return matchSearch && matchCategory
    })
  }, [products, searchTerm, selectedCategory, localHiddenProductIds])

  // Select all / deselect all
  const handleToggleSelectAll = () => {
    const visibleIds = filteredProducts.map((p) => p.id)
    const isAllVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))
    if (isAllVisibleSelected) {
      setSelectedIds(selectedIds.filter((id) => !visibleIds.includes(id)))
    } else {
      setSelectedIds(Array.from(new Set([...selectedIds, ...visibleIds])))
    }
  }

  const handleToggleRow = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const parsedAltBarcodes = altBarcodes
      ? altBarcodes.split(',').map((b) => b.trim()).filter(Boolean)
      : []

    try {
      const created = await api.createProduct({
        code,
        name,
        category_id: categoryId || null,
        image: image || null,
        unit,
        barcode: barcode || null,
        alternatives_barcodes: parsedAltBarcodes,
        kdv_rate: parseFloat(kdvRate) || 0.20,
        maliyet: 0,
        recipe_output_quantity: parseFloat(recipeOutputQuantity) || 1.0,
        calculation_method_override: methodOverride ? (methodOverride as any) : null,
      })
      if (setProducts) {
        const cat = categories.find((c) => c.id === created.category_id)
        setProducts((prev) => [{ ...created, category: cat || created.category }, ...prev])
      } else {
        await onRefresh()
      }
      setShowAddModal(false)
      setCode('')
      setName('')
      setImage('')
      setUnit('Adet')
      setBarcode('')
      setAltBarcodes('')
      setKdvRate('0.20')
      setRecipeOutputQuantity('1')
      setMethodOverride('')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openEditModal = (p: Product) => {
    setEditingProduct(p)
    setEditCode(p.code)
    setEditName(p.name)
    setEditCategoryId(p.category_id || '')
    setEditImage(p.image || '')
    setEditUnit(p.unit || 'Adet')
    setEditBarcode(p.barcode || '')
    setEditAltBarcodes(p.alternatives_barcodes?.join(', ') || '')
    setEditKdvRate(p.kdv_rate !== undefined && p.kdv_rate !== null ? String(p.kdv_rate) : '0.20')
    setEditRecipeOutputQuantity(String(p.recipe_output_quantity || '1'))
    setEditMethodOverride(p.calculation_method_override || '')
  }

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingProduct) return
    setError(null)
    const parsedAltBarcodes = editAltBarcodes
      ? editAltBarcodes.split(',').map((b) => b.trim()).filter(Boolean)
      : []

    try {
      const updated = await api.updateProduct(editingProduct.id, {
        code: editCode,
        name: editName,
        category_id: editCategoryId || null,
        image: editImage || null,
        unit: editUnit,
        barcode: editBarcode || null,
        alternatives_barcodes: parsedAltBarcodes,
        kdv_rate: parseFloat(editKdvRate) || 0.20,
        maliyet: 0,
        recipe_output_quantity: parseFloat(editRecipeOutputQuantity) || 1.0,
        calculation_method_override: editMethodOverride ? (editMethodOverride as any) : null,
      })
      setEditingProduct(null)
      if (setProducts) {
        const cat = categories.find((c) => c.id === updated.category_id)
        setProducts((prev) =>
          prev.map((p) =>
            p.id === updated.id
              ? {
                  ...p,
                  ...updated,
                  category: cat || updated.category,
                  components: p.components,
                }
              : p
          )
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSaveSingleCellEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!singleCellEdit) return
    setError(null)

    try {
      const payload: Partial<Product> = {}
      const val = singleCellEdit.value

      if (singleCellEdit.field === 'kdv_rate') {
        payload.kdv_rate = parseFloat(val) || 0
      } else if (singleCellEdit.field === 'category_id') {
        payload.category_id = val || null
      } else if (singleCellEdit.field === 'unit') {
        payload.unit = val
      } else if (singleCellEdit.field === 'code') {
        if (!val.trim()) throw new Error('Ürün kodu boş bırakılamaz.')
        payload.code = val.trim()
      } else if (singleCellEdit.field === 'name') {
        if (!val.trim()) throw new Error('Ürün adı boş bırakılamaz.')
        payload.name = val.trim()
      } else if (singleCellEdit.field === 'barcode') {
        payload.barcode = val.trim() || null
      } else if (singleCellEdit.field === 'image') {
        payload.image = val || null
      }

      const updated = await api.updateProduct(singleCellEdit.product.id, payload)
      setSingleCellEdit(null)
      if (setProducts) {
        const cat = categories.find((c) => c.id === updated.category_id)
        setProducts((prev) =>
          prev.map((p) =>
            p.id === updated.id
              ? {
                  ...p,
                  ...updated,
                  category: cat || updated.category,
                  components: p.components,
                }
              : p
          )
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    }
  }

  const openBulkEditModal = (field: string, label: string) => {
    setBulkEditField({ field, label })
    setBulkUnit('Adet')
    setBulkCategory('')
    setBulkKdv('0.20')
  }

  const handleApplyBulkEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!bulkEditField || selectedIds.length === 0) return
    setIsProcessing(true)
    setError(null)
    try {
      const field = bulkEditField.field
      const payload: any = {
        product_ids: selectedIds,
        field,
      }

      if (field === 'unit') {
        payload.unit_value = bulkUnit
      } else if (field === 'category_id') {
        payload.category_id_value = bulkCategory || null
      } else if (field === 'kdv_rate') {
        payload.kdv_rate_value = parseFloat(bulkKdv) || 0.20
      }

      await api.bulkUpdateProducts(payload)
      setBulkEditField(null)
      if (onRefreshProducts) {
        await onRefreshProducts()
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return
    const itemToDelete = products.find((p) => p.id === id)
    const originalIndex = products.findIndex((p) => p.id === id)

    // Optimistically remove from local copy of table immediately
    if (setProducts) {
      setProducts((prev) => prev.filter((item) => item.id !== id))
    }
    setLocalHiddenProductIds((prev) => new Set(prev).add(id))
    setSelectedIds((prev) => prev.filter((item) => item !== id))

    try {
      await api.deleteProduct(id)
    } catch (err: any) {
      // Revert/restore item if deletion fails or is rejected by server
      if (setProducts && itemToDelete) {
        setProducts((prev) => {
          if (prev.some((item) => item.id === id)) return prev
          const next = [...prev]
          next.splice(originalIndex >= 0 ? originalIndex : 0, 0, itemToDelete)
          return next
        })
      }
      setLocalHiddenProductIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenProductIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }

  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleExportExcel = async () => {
    setIsProcessing(true)
    try {
      await api.exportProductsExcel()
    } catch (err: any) {
      alert(`Excel indirme hatası: ${err.message}`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const confirmed = window.confirm(
      'Excel dosyasındaki mamul ürünler ve reçete ağaçları sisteme aktarılacaktır.\n\n' +
      '• Eşleşen ürünler ve reçete bileşenleri güncellenecek, yeni ürünler eklenecektir.\n' +
      '• Excel dosyasında yer almayan mevcut kayıtlar silinmeyecektir (korunacaktır).\n\n' +
      'Devam etmek istiyor musunuz?'
    )
    if (!confirmed) {
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      const res = await api.importProductsExcel(file)
      alert(`Excel başarıyla işlendi:\n${res.created} yeni eklendi, ${res.updated} güncellendi.`)
      if (onCascadeRefresh) {
        await onCascadeRefresh()
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsProcessing(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // --- Components Management (Cascading Tree & Root-Only Addition) ---
  const openAddComponentModal = (p: Product) => {
    setActiveProductForAddComponent(p)
    setSelectedCompType('raw_material')
    setSelectedItemId('')
    setCompQuantity('1.0000')
    setCompUnitMode('kg')
    setCompError(null)
  }

  const handleAddComponent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeProductForAddComponent || !selectedItemId) return
    setCompError(null)
    try {
      const selectedUnit = getSelectedComponentUnit()
      const isKg = selectedUnit.trim().toLowerCase() === 'kg'
      const cleanQty = compQuantity.replace(',', '.')
      let numVal = parseFloat(cleanQty) || 1.0
      if (isKg && compUnitMode === 'g') {
        numVal = numVal / 1000.0
      }

      const parentN = Number(activeProductForAddComponent.recipe_output_quantity || 1)
      const mVal = numVal
      const unitAmt = parentN > 0 ? mVal / parentN : mVal

      const newComp = await api.addProductComponent(activeProductForAddComponent.id, {
        component_type: selectedCompType,
        component_id: selectedItemId,
        recipe_amount: mVal,
        unit_amount: unitAmt,
        quantity: unitAmt,
      })
      const targetId = activeProductForAddComponent.id
      setSelectedItemId('')
      setCompQuantity('1.0000')
      setCompUnitMode('kg')
      setActiveProductForAddComponent(null)
      // Keep root node open
      setExpandedKeys((prev) => new Set(prev).add(`p_${targetId}`))
      if (setProducts) {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id !== targetId) return p
            return {
              ...p,
              components: [...(p.components || []), newComp],
            }
          })
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      setCompError(err.message)
    }
  }

  const handleDeleteComponent = async (productId: string, compId: string) => {
    if (!confirm('Bu bileşeni mamulden çıkarmak istediğinizden emin misiniz?')) return
    const parentProduct = products.find((p) => p.id === productId)
    const compToDelete = parentProduct?.components?.find((c) => c.id === compId)
    const originalIndex = parentProduct?.components?.findIndex((c) => c.id === compId) ?? -1

    if (setProducts) {
      setProducts((prev) =>
        prev.map((p) => {
          if (p.id !== productId) return p
          return {
            ...p,
            components: (p.components || []).filter((c) => c.id !== compId),
          }
        })
      )
    }
    setLocalHiddenComponentIds((prev) => new Set(prev).add(compId))

    try {
      await api.deleteProductComponent(productId, compId)
    } catch (err: any) {
      if (setProducts && compToDelete && originalIndex >= 0) {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id !== productId) return p
            const comps = [...(p.components || [])]
            if (!comps.some((c) => c.id === compId)) {
              comps.splice(originalIndex, 0, compToDelete)
            }
            return { ...p, components: comps }
          })
        )
      }
      setLocalHiddenComponentIds((prev) => {
        const next = new Set(prev)
        next.delete(compId)
        return next
      })
      alert(err.message)
      return
    }

    setLocalHiddenComponentIds((prev) => {
      const next = new Set(prev)
      next.delete(compId)
      return next
    })
  }

  const handleSaveComponentQuantity = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCompQuantity) return
    const parentN = Number(editingCompQuantity.product.recipe_output_quantity || 1)
    const mVal = parseFloat(editingCompQuantity.quantity) || 1.0
    const unitAmt = parentN > 0 ? mVal / parentN : mVal
    try {
      const updatedComp = await api.updateProductComponent(
        editingCompQuantity.product.id,
        editingCompQuantity.component.id,
        {
          recipe_amount: mVal,
          unit_amount: unitAmt,
          quantity: unitAmt,
        }
      )
      const targetProductId = editingCompQuantity.product.id
      const targetCompId = editingCompQuantity.component.id
      setEditingCompQuantity(null)
      if (setProducts) {
        setProducts((prev) =>
          prev.map((p) => {
            if (p.id !== targetProductId) return p
            return {
              ...p,
              components: (p.components || []).map((c) =>
                c.id === targetCompId
                  ? {
                      ...c,
                      ...updatedComp,
                      recipe_amount: mVal,
                      unit_amount: unitAmt,
                      quantity: unitAmt,
                    }
                  : c
              ),
            }
          })
        )
      } else {
        await onRefresh()
      }
    } catch (err: any) {
      alert(err.message)
    }
  }

  const getSelectableItems = (): SelectableComponentOption[] => {
    if (selectedCompType === 'raw_material') {
      return rawMaterials.map((r) => ({
        id: r.id,
        code: r.code,
        name: r.name,
        unit: r.unit,
        maliyet: Number(r.maliyet || 0),
        typeLabel: 'Hammadde',
        label: `[Hammadde] ${r.code} — ${r.name} (₺${Number(r.maliyet).toFixed(2)} / ${r.unit})`,
      }))
    } else if (selectedCompType === 'packaging') {
      return packagings.map((p) => ({
        id: p.id,
        code: p.code,
        name: p.name,
        unit: p.unit,
        maliyet: Number(p.maliyet || 0),
        typeLabel: 'Ambalaj',
        label: `[Ambalaj] ${p.code} — ${p.name} (₺${Number(p.maliyet).toFixed(2)} / ${p.unit})`,
      }))
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished.map((s) => ({
        id: s.id,
        code: s.code,
        name: s.name,
        unit: s.unit,
        maliyet: Number(s.maliyet || 0),
        typeLabel: 'Yarı Mamul',
        label: `[Yarı Mamul] ${s.code} — ${s.name} (₺${Number(s.maliyet).toFixed(2)} / ${s.unit})`,
      }))
    } else if (selectedCompType === 'product') {
      return products
        .filter((p) => p.id !== activeProductForAddComponent?.id)
        .map((p) => ({
          id: p.id,
          code: p.code,
          name: p.name,
          unit: p.unit,
          maliyet: Number(p.maliyet || 0),
          typeLabel: 'Mamul Ürün',
          label: `[Mamul Ürün] ${p.code} — ${p.name} (₺${Number(p.maliyet).toFixed(2)} / ${p.unit})`,
        }))
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).map((t) => ({
        id: t.id,
        code: t.code,
        name: t.name,
        unit: t.unit || 'Adet',
        maliyet: Number(t.maliyet || 0),
        typeLabel: 'Al-Sat Ürün',
        label: `[Al-Sat Ürün] ${t.code} — ${t.name} (₺${Number(t.maliyet).toFixed(2)} / ${t.unit || 'Adet'})`,
      }))
    }
    return []
  }

  const getSelectedComponentUnit = (): string => {
    if (!selectedItemId) return ''
    if (selectedCompType === 'raw_material') {
      return rawMaterials.find((r) => r.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'packaging') {
      return packagings.find((p) => p.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'semi_finished') {
      return semiFinished.find((s) => s.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'product') {
      return products.find((p) => p.id === selectedItemId)?.unit || ''
    } else if (selectedCompType === 'trading_good') {
      return (tradingGoods || []).find((t) => t.id === selectedItemId)?.unit || ''
    }
    return ''
  }

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'raw_material':
        return <span className="badge badge-info">Hammadde</span>
      case 'packaging':
        return <span className="badge badge-warning">Ambalaj</span>
      case 'semi_finished':
        return <span className="badge badge-primary">Yarı Mamul</span>
      case 'product':
        return <span className="badge badge-success">Mamul Ürün</span>
      case 'trading_good':
        return <span className="badge badge-purple" style={{ backgroundColor: '#6f42c1', color: '#fff' }}>Al-Sat</span>
      default:
        return <span className="badge badge-secondary">{type}</span>
    }
  }

  // Recursively compute the unit cost of a component including all its sub-components
  const getRecursiveUnitCost = React.useCallback(
    (
      componentType: ComponentType,
      componentId: string,
      baseCost: number,
      visited: Set<string> = new Set()
    ): number => {
      if (visited.has(componentId)) {
        return baseCost
      }

      if (componentType === 'raw_material' || componentType === 'packaging' || componentType === 'trading_good') {
        return baseCost
      }

      const nextVisited = new Set(visited)
      nextVisited.add(componentId)

      let subCost = 0
      if (componentType === 'semi_finished') {
        const semi = semiFinishedMap.get(componentId)
        const directBase = Number(baseCost || semi?.maliyet || 0)
        const subComps = semi?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      if (componentType === 'product') {
        const prod = productsMap.get(componentId)
        const directBase = Number(baseCost || prod?.maliyet || 0)
        const subComps = prod?.components || []
        for (const child of subComps) {
          const childQty = Number(child.unit_amount || child.quantity || 0)
          const childBase = Number(child.birim_maliyet || 0)
          const childUnitCost = getRecursiveUnitCost(
            child.component_type,
            child.component_id,
            childBase,
            nextVisited
          )
          subCost += childQty * childUnitCost
        }
        return directBase + subCost
      }

      return baseCost
    },
    [semiFinishedMap, productsMap]
  )

  const renderComponentTree = (
    rootProduct: Product,
    components: ComponentItem[],
    depth: number,
    parentPath: string,
    ancestorIds: Set<string>
  ): React.ReactNode[] => {
    return components
      .filter((comp) => !localHiddenComponentIds.has(comp.id))
      .map((comp) => {
      const childPath = `${parentPath}/${comp.id}`
      const isNode = comp.component_type === 'semi_finished' || comp.component_type === 'product'
      const isExpanded = expandedKeys.has(childPath)
      const isDirectChild = depth === 1
      const unitAmountNum = Number(comp.unit_amount || comp.quantity || 0)
      const unitCostNum = Number(comp.birim_maliyet || 0)
      const recursiveUnitCost = getRecursiveUnitCost(
        comp.component_type,
        comp.component_id,
        unitCostNum,
        ancestorIds
      )
      const totalContribution = unitAmountNum * recursiveUnitCost

      // Look up sub-components if node
      let subComponents: ComponentItem[] = []
      let hasCycle = false
      if (isNode) {
        if (ancestorIds.has(comp.component_id)) {
          hasCycle = true
        } else if (comp.component_type === 'semi_finished') {
          subComponents = semiFinishedMap.get(comp.component_id)?.components || []
        } else if (comp.component_type === 'product') {
          subComponents = productsMap.get(comp.component_id)?.components || []
        }
      }

      return (
        <div key={childPath} className="bom-tree-item-wrapper">
          <div
            className={`bom-tree-row bom-tree-depth-${Math.min(depth, 3)}`}
            style={{ paddingLeft: `${(depth - 1) * 24 + 10}px` }}
          >
            <div className="bom-row-left">
              {isNode ? (
                <button
                  type="button"
                  className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                  aria-label={
                    isExpanded
                      ? `${comp.name} alt bileşenlerini daralt`
                      : `${comp.name} alt bileşenlerini genişlet`
                  }
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(childPath)}
                >
                  <ChevronIcon />
                </button>
              ) : (
                <span className="tree-leaf-spacer" aria-hidden="true" />
              )}
              {getTypeBadge(comp.component_type)}
              <code className="bom-code-badge">{comp.code}</code>
              <span className="bom-item-name" style={{ fontWeight: isDirectChild ? 600 : 400 }}>
                {comp.name}
              </span>
              {comp.component_type === 'semi_finished' && (() => {
                const sf = semiFinishedMap.get(comp.component_id)
                if (!sf || !isSemiFinishedEligibleForRecipeReport(sf)) return null
                return (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenReport?.(sf, 'semi_finished')
                    }}
                    title="Yarı Mamul Reçete Raporu"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 4px',
                      marginLeft: '6px',
                      lineHeight: 1,
                      verticalAlign: 'middle',
                    }}
                  >
                    <PieChartIcon size={12} style={{ marginRight: 0 }} />
                  </button>
                )
              })()}
            </div>

            <div className="bom-row-right">
              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Miktar:</span>
                <span className="tabular-nums bom-pill-value">
                  {formatQuantity(unitAmountNum)} {comp.unit}
                </span>
                {isDirectChild && (
                  <button
                    type="button"
                    className="cell-edit-btn"
                    aria-label={`${comp.name} birim miktarını düzenle`}
                    onClick={() => {
                      const parentN = Number(rootProduct.recipe_output_quantity || 1)
                      const mVal = comp.recipe_amount != null ? Number(comp.recipe_amount) : unitAmountNum * parentN
                      setEditingCompQuantity({
                        product: rootProduct,
                        component: comp,
                        quantity: String(mVal),
                      })
                    }}
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Birim Maliyet:</span>
                <span className="tabular-nums bom-pill-value">₺{unitCostNum.toFixed(2)}</span>
              </div>

              <div className="bom-pill-metric">
                <span className="bom-pill-label">Toplam Maliyet:</span>
                <span
                  className="tabular-nums bom-pill-value"
                  style={{ color: 'var(--color-primary)', fontWeight: 600 }}
                >
                  ₺{totalContribution.toFixed(2)}
                </span>
              </div>

              <div className="bom-row-actions">
                {isDirectChild ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                    aria-label={`${comp.name} bileşenini kaldır`}
                    onClick={() => handleDeleteComponent(rootProduct.id, comp.id)}
                  >
                    ✕ Kaldır
                  </button>
                ) : (
                  <span
                    className="text-muted"
                    style={{ fontSize: '0.75rem', width: '50px', textAlign: 'center', display: 'inline-block' }}
                    title="Alt yarı mamullerin bileşenleri Yarı Mamuller sekmesinden yönetilir"
                  >
                    —
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Sub-components */}
          {isNode && isExpanded && (
            <div className="bom-sub-tree">
              {hasCycle ? (
                <div
                  className="bom-tree-alert"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  ⚠️ Döngüsel referans algılandı ({comp.name})
                </div>
              ) : subComponents.length === 0 ? (
                <div
                  className="bom-tree-empty-hint"
                  style={{ paddingLeft: `${depth * 24 + 34}px` }}
                >
                  Bu alt bileşenin tanımlı bir reçetesi bulunmuyor.
                </div>
              ) : (
                renderComponentTree(
                  rootProduct,
                  subComponents,
                  depth + 1,
                  childPath,
                  new Set([...ancestorIds, comp.component_id])
                )
              )}
            </div>
          )}
        </div>
      )
    })
  }


  const hasSelection = selectedIds.length > 0

  return (
    <div className="view-container">
      <div className="section-header-flex">
        <div>
          <h2>Mamul Ürünler</h2>
        </div>
        <div className="header-actions">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept=".xlsx, .xls"
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessing}
            title="Excel dosyasından mamulleri ve reçeteleri yükle"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Excel Yükle</span>
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleExportExcel}
            disabled={isProcessing}
            title="Mamulleri ve reçete ağacını Excel olarak indir"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            <span>Excel İndir</span>
          </button>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Yeni Mamul Ekle
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Mamul adı veya koduna göre ara…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <select
          className="form-control"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '220px' }}
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="table-card">
        <div className="table-toolbar">
          <div className="selection-info">
            {hasSelection ? (
              <span><strong>{selectedIds.length}</strong> ürün seçildi</span>
            ) : (
              <span className="text-muted">Toplu işlem için soldaki kutucukları işaretleyin</span>
            )}
          </div>
          <div className="toolbar-actions">
            <div className="dropdown">
              <button className="btn btn-secondary" disabled={!hasSelection}>
                Toplu Düzenle ▼
              </button>
              <div className="dropdown-menu">
                <button
                  className="dropdown-item"
                  disabled={!hasSelection}
                  onClick={() => hasSelection && openBulkEditModal('unit', 'Ölçü Birimi')}
                >
                  Ölçü Birimi (Adet/Kg/Lt)
                </button>
                <button
                  className="dropdown-item"
                  disabled={!hasSelection}
                  onClick={() => hasSelection && openBulkEditModal('category_id', 'Kategori')}
                >
                  Kategori Ata
                </button>
                <button
                  className="dropdown-item"
                  disabled={!hasSelection}
                  onClick={() => hasSelection && openBulkEditModal('kdv_rate', 'KDV Oranı')}
                >
                  KDV Oranı Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="empty-state">
            <p>{products.length === 0 ? 'Kayıtlı mamul ürün bulunmuyor.' : 'Arama kriterlerine uygun mamul ürün bulunamadı.'}</p>
          </div>
        ) : (
          <table className="data-table products-table">
            <thead>
              <tr>
                <th style={{ width: '38px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={filteredProducts.length > 0 && filteredProducts.every((p) => selectedIds.includes(p.id))}
                    onChange={handleToggleSelectAll}
                    aria-label="Tüm ürünleri seç"
                  />
                </th>
                <th style={{ minWidth: '130px' }}>Kod</th>
                <th style={{ minWidth: '180px' }}>Ürün Adı</th>
                <th style={{ minWidth: '110px' }}>Kategori</th>
                <th style={{ minWidth: '70px' }}>Birim</th>
                <th style={{ minWidth: '110px' }}>Maliyet</th>
                <th style={{ minWidth: '140px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => {
                const isSelected = selectedIds.includes(p.id)
                const rootKey = `p_${p.id}`
                const isExpanded = expandedKeys.has(rootKey)
                const comps = (p.components || []).filter((c) => !localHiddenComponentIds.has(c.id))

                return (
                  <React.Fragment key={p.id}>
                    <tr className={isSelected ? 'row-selected' : ''}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleRow(p.id)}
                          aria-label={`${p.name} ürününü seç`}
                        />
                      </td>
                      <td>
                        <div className="tree-cell-flex">
                          <button
                            type="button"
                            className={`tree-toggle-btn ${isExpanded ? 'expanded' : ''}`}
                            aria-label={
                              isExpanded
                                ? `${p.name} bileşenlerini daralt`
                                : `${p.name} bileşenlerini genişlet`
                            }
                            aria-expanded={isExpanded}
                            onClick={() => toggleExpand(rootKey)}
                          >
                            <ChevronIcon />
                          </button>
                          <div className="cell-editable">
                            <strong>{p.code}</strong>
                            <button
                              type="button"
                              className="cell-edit-btn"
                              aria-label="Ürün kodunu düzenle"
                              onClick={() =>
                                setSingleCellEdit({
                                 product: p,
                                  field: 'code',
                                  label: 'Ürün Kodu',
                                  value: p.code,
                                })
                              }
                            >
                              <PencilIcon />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="cell-editable" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {p.image && (
                            <img
                              src={p.image}
                              alt=""
                              style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <span>{p.name}</span>
                          <button
                            type="button"
                            className="cell-edit-btn"
                            aria-label="Ürün adını düzenle"
                            onClick={() =>
                              setSingleCellEdit({
                                product: p,
                                field: 'name',
                                label: 'Ürün Adı',
                                value: p.name,
                              })
                            }
                          >
                            <PencilIcon />
                          </button>
                        </div>
                      </td>
                      <td>{p.category?.name || <span className="text-muted">—</span>}</td>
                      <td>
                        <span className="badge badge-secondary">{p.unit || 'Adet'}</span>
                      </td>
                      <td className="tabular-nums">
                        <strong>₺{calculateProductTotalCost(p, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                      </td>
                      <td>
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => openAddComponentModal(p)}
                            title="Bu mamulün reçetesine yeni bileşen ekle"
                          >
                            + Bileşen
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation()
                              setGearMenu({ product: p, x: e.clientX, y: e.clientY })
                            }}
                            title="İşlemler (Düzenle / Demo Ortamına Kopyala)"
                          >
                            <SettingsGearIcon />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteProduct(p.id)}
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Cascading Tree Container */}
                    {isExpanded && (
                      <tr className="bom-expansion-row">
                        <td colSpan={9} className="bom-expansion-cell">
                          <div className="bom-dropdown-container">
                            {comps.length === 0 ? (
                              <div className="bom-empty-state">
                                <span>Bu ürüne henüz bir bileşen eklenmemiş.</span>
                                <button
                                  type="button"
                                  className="tree-add-btn"
                                  onClick={() => openAddComponentModal(p)}
                                >
                                  + İlk Bileşeni Ekle
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="bom-tree-list">
                                  {renderComponentTree(p, comps, 1, rootKey, new Set([p.id]))}
                                </div>
                                <div className="bom-tree-add-footer">
                                  <button
                                    type="button"
                                    className="tree-add-btn"
                                    onClick={() => openAddComponentModal(p)}
                                  >
                                    + <strong>{p.name}</strong> İçin Yeni Bileşen Ekle
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Yeni Mamul Ürün Ekle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateProduct}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-prod-code">Ürün Kodu *</label>
                    <input
                      id="add-prod-code"
                      name="code"
                      required
                      autoComplete="off"
                      spellCheck={false}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Örn: ELK-101…"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-prod-name">Ürün Adı *</label>
                    <input
                      id="add-prod-name"
                      name="name"
                      required
                      autoComplete="off"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Örn: Akıllı Saat…"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-prod-category">Kategori</label>
                    <select
                      id="add-prod-category"
                      name="category_id"
                      value={categoryId}
                      onChange={(e) => setCategoryId(e.target.value)}
                    >
                      <option value="">Kategori Seçin…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-prod-unit">Ölçü Birimi</label>
                    <select
                      id="add-prod-unit"
                      name="unit"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                    >
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-prod-recipe-output">
                      Reçete Sonucu Oluşan Mamul Miktarı ({unit}) *
                    </label>
                    <input
                      id="add-prod-recipe-output"
                      name="recipe_output_quantity"
                      type="number"
                      step="any"
                      min="0.0001"
                      required
                      autoComplete="off"
                      value={recipeOutputQuantity}
                      onChange={(e) => setRecipeOutputQuantity(e.target.value)}
                      placeholder="1.0000…"
                    />
                    <span className="helper-text">
                      Reçete bileşenleri bu miktara göre girilir
                    </span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-prod-kdv">KDV Oranı</label>
                    <select
                      id="add-prod-kdv"
                      name="kdv_rate"
                      value={kdvRate}
                      onChange={(e) => setKdvRate(e.target.value)}
                    >
                      <option value="0.20">%20</option>
                      <option value="0.10">%10</option>
                      <option value="0.01">%1</option>
                      <option value="0.00">%0</option>
                    </select>
                  </div>
                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-prod-image">Görsel URL</label>
                    <input
                      id="add-prod-image"
                      name="image"
                      autoComplete="off"
                      spellCheck={false}
                      value={image}
                      onChange={(e) => setImage(e.target.value)}
                      placeholder="https://…"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-prod-barcode">Barkod</label>
                    <input
                      id="add-prod-barcode"
                      name="barcode"
                      autoComplete="off"
                      spellCheck={false}
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      placeholder="8690…"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Mamul Ürünü Düzenle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setEditingProduct(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleUpdateProduct}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-prod-code">Ürün Kodu *</label>
                    <input
                      id="edit-prod-code"
                      name="code"
                      required
                      autoComplete="off"
                      spellCheck={false}
                      value={editCode}
                      onChange={(e) => setEditCode(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-prod-name">Ürün Adı *</label>
                    <input
                      id="edit-prod-name"
                      name="name"
                      required
                      autoComplete="off"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-prod-category">Kategori</label>
                    <select
                      id="edit-prod-category"
                      name="category_id"
                      value={editCategoryId}
                      onChange={(e) => setEditCategoryId(e.target.value)}
                    >
                      <option value="">Kategori Seçin…</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-prod-unit">Ölçü Birimi</label>
                    <select
                      id="edit-prod-unit"
                      name="unit"
                      value={editUnit}
                      onChange={(e) => setEditUnit(e.target.value)}
                    >
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-prod-recipe-output">
                      Reçete Sonucu Oluşan Mamul Miktarı ({editUnit}) *
                    </label>
                    <input
                      id="edit-prod-recipe-output"
                      name="recipe_output_quantity"
                      type="number"
                      step="any"
                      min="0.0001"
                      required
                      autoComplete="off"
                      value={editRecipeOutputQuantity}
                      onChange={(e) => setEditRecipeOutputQuantity(e.target.value)}
                    />
                    <span className="helper-text">
                      Reçetenin ürettiği toplam mamul miktarı
                    </span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-prod-kdv">KDV Oranı</label>
                    <select
                      id="edit-prod-kdv"
                      name="kdv_rate"
                      value={editKdvRate}
                      onChange={(e) => setEditKdvRate(e.target.value)}
                    >
                      <option value="0.20">%20</option>
                      <option value="0.10">%10</option>
                      <option value="0.01">%1</option>
                      <option value="0.00">%0</option>
                    </select>
                  </div>
                </div>


                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-prod-image">Görsel URL</label>
                    <input
                      id="edit-prod-image"
                      name="image"
                      autoComplete="off"
                      spellCheck={false}
                      value={editImage}
                      onChange={(e) => setEditImage(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-prod-barcode">Barkod</label>
                    <input
                      id="edit-prod-barcode"
                      name="barcode"
                      autoComplete="off"
                      spellCheck={false}
                      value={editBarcode}
                      onChange={(e) => setEditBarcode(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingProduct(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Single Cell Quick Edit Modal */}
      {singleCellEdit && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>{singleCellEdit.label} Düzenle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setSingleCellEdit(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveSingleCellEdit}>
              <div className="modal-body">
                <p className="subtitle">
                  <strong>{singleCellEdit.product.name}</strong> ({singleCellEdit.product.code})
                </p>
                <div className="form-group">
                  <label htmlFor="single-cell-input">{singleCellEdit.label}</label>
                  <input
                    id="single-cell-input"
                    name="single_cell_value"
                    type="text"
                    required
                    autoComplete="off"
                    value={singleCellEdit.value}
                    onChange={(e) => setSingleCellEdit({ ...singleCellEdit, value: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setSingleCellEdit(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {bulkEditField && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-header">
              <h3>Toplu Düzenle: {bulkEditField.label}</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setBulkEditField(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleApplyBulkEdit}>
              <div className="modal-body">
                <p>
                  Seçilen <strong>{selectedIds.length}</strong> ürün için <strong>{bulkEditField.label}</strong> alanı güncellenecektir.
                </p>

                {bulkEditField.field === 'unit' && (
                  <div className="form-group">
                    <label>Yeni Ölçü Birimi</label>
                    <select value={bulkUnit} onChange={(e) => setBulkUnit(e.target.value)}>
                      <option value="Adet">Adet</option>
                      <option value="Kg">Kg</option>
                      <option value="Lt">Lt</option>
                    </select>
                  </div>
                )}

                {bulkEditField.field === 'category_id' && (
                  <div className="form-group">
                    <label>Yeni Kategori</label>
                    <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
                      <option value="">Kategori Seçin...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {bulkEditField.field === 'kdv_rate' && (
                  <div className="form-group">
                    <label>Yeni KDV Oranı</label>
                    <select value={bulkKdv} onChange={(e) => setBulkKdv(e.target.value)}>
                      <option value="0.20">%20</option>
                      <option value="0.10">%10</option>
                      <option value="0.01">%1</option>
                      <option value="0.00">%0</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setBulkEditField(null)}>İptal</button>
                <button type="submit" className="btn btn-primary" disabled={isProcessing}>
                  {isProcessing ? 'Uygulanıyor...' : 'Değişiklikleri Uygula'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Component to Root Product Modal */}
      {activeProductForAddComponent && (
        <div className="modal-backdrop">
          <div className="modal-box modal-lg">
            <div className="modal-header">
              <div>
                <h3>Reçeteye Yeni Bileşen Ekle</h3>
                <p className="subtitle" style={{ margin: 0 }}>
                  <strong>{activeProductForAddComponent.name}</strong> ({activeProductForAddComponent.code}) için girdi bileşeni
                </p>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setActiveProductForAddComponent(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddComponent}>
              <div className="modal-body">
                {compError && (
                  <div className="alert alert-danger" aria-live="polite">
                    {compError}
                  </div>
                )}

                <div className="form-row">
                  {/* Step 1: Component Category / Type */}
                  <div className="form-group">
                    <label htmlFor="comp-type-select">1. Bileşen Türü *</label>
                    <select
                      id="comp-type-select"
                      name="component_type"
                      value={selectedCompType}
                      onChange={(e) => {
                        setSelectedCompType(e.target.value as ComponentType)
                        setSelectedItemId('')
                      }}
                    >
                      <option value="raw_material">Hammadde</option>
                      <option value="packaging">Ambalaj</option>
                      <option value="semi_finished">Yarı Mamul</option>
                      <option value="product">Mamul Ürün</option>
                      <option value="trading_good">Al-Sat Ürün</option>
                    </select>
                  </div>

                  {/* Step 2: Item Selection from Filtered Category */}
                  <div className="form-group" style={{ flex: 2 }}>
                    <label htmlFor="comp-item-select">2. Kayıt Seçin *</label>
                    <SearchableComponentSelect
                      id="comp-item-select"
                      options={getSelectableItems()}
                      value={selectedItemId}
                      onChange={(id) => {
                        setSelectedItemId(id)
                        setCompUnitMode('kg')
                      }}
                      placeholder="Bileşen adı veya kodu ile arayın…"
                      required
                    />
                  </div>
                </div>

                {(() => {
                  const selectedUnit = getSelectedComponentUnit()
                  const isKg = selectedUnit.trim().toLowerCase() === 'kg'
                  return (
                    <div className="form-row" style={{ marginTop: '1rem', flexDirection: 'column' }}>
                      {isKg && (
                        <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Giriş Birimi:</span>
                          <div className="btn-group" role="group" style={{ display: 'inline-flex' }}>
                            <button
                              type="button"
                              className={`btn btn-sm ${compUnitMode === 'kg' ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={() => setCompUnitMode('kg')}
                            >
                              Kg
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${compUnitMode === 'g' ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={() => setCompUnitMode('g')}
                            >
                              g (Gram)
                            </button>
                          </div>
                          {compUnitMode === 'g' && (
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                              (Kaydedilirken Kg'ye çevrilecektir: {parseFloat(compQuantity.replace(',', '.')) ? formatQuantity(parseFloat(compQuantity.replace(',', '.')) / 1000) : '0'} Kg)
                            </span>
                          )}
                        </div>
                      )}

                      {/* Step 3: Quantity / Amount per n */}
                      <div className="form-group" style={{ width: '100%' }}>
                        <label htmlFor="comp-unit-amount">
                          3. {Number(activeProductForAddComponent.recipe_output_quantity || 1)} {activeProductForAddComponent.unit} Başına Miktar ({isKg && compUnitMode === 'g' ? 'Gram' : selectedUnit || 'Birim'}) *
                        </label>
                        <input
                          id="comp-unit-amount"
                          name="unit_amount"
                          type="number"
                          step="any"
                          min="0.0001"
                          required
                          autoComplete="off"
                          value={compQuantity}
                          onChange={(e) => setCompQuantity(e.target.value)}
                          placeholder={isKg && compUnitMode === 'g' ? '0.6…' : '1.0000…'}
                        />
                        <span className="helper-text">
                          {Number(activeProductForAddComponent.recipe_output_quantity || 1)} {activeProductForAddComponent.unit} {activeProductForAddComponent.name} üretmek için gereken miktar
                        </span>
                      </div>
                    </div>
                  )
                })()}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActiveProductForAddComponent(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Bileşeni Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Component Quantity Modal */}
      {editingCompQuantity && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3>Bileşen Miktarını Düzenle</h3>
              <button
                type="button"
                className="modal-close"
                aria-label="Kapat"
                onClick={() => setEditingCompQuantity(null)}
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSaveComponentQuantity}>
              <div className="modal-body">
                <p className="subtitle">
                  <strong>{editingCompQuantity.product.name}</strong> için girdi olan{' '}
                  <strong>{editingCompQuantity.component.name}</strong> ({editingCompQuantity.component.code})
                </p>
                <div className="form-group">
                  <label htmlFor="edit-comp-quantity">
                    {Number(editingCompQuantity.product.recipe_output_quantity || 1)} {editingCompQuantity.product.unit} Başına Miktar ({editingCompQuantity.component.unit})
                  </label>
                  <input
                    id="edit-comp-quantity"
                    name="quantity"
                    type="number"
                    step="0.000001"
                    min="0.000001"
                    required
                    autoComplete="off"
                    value={editingCompQuantity.quantity}
                    onChange={(e) =>
                      setEditingCompQuantity({
                        ...editingCompQuantity,
                        quantity: e.target.value,
                      })
                    }
                  />
                  <span className="helper-text" style={{ fontSize: '0.8rem', marginTop: '0.25rem', display: 'block', color: 'var(--color-text-muted)' }}>
                    Birim miktar (m/n): {(parseFloat(editingCompQuantity.quantity) ? formatQuantity(parseFloat(editingCompQuantity.quantity) / Number(editingCompQuantity.product.recipe_output_quantity || 1)) : '0')} {editingCompQuantity.component.unit}
                  </span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingCompQuantity(null)}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gear Context Menu */}
      {gearMenu && (
        <div
          className="gear-context-menu-backdrop"
          style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
          onClick={() => setGearMenu(null)}
        >
          <div
            className="gear-dropdown-menu"
            style={{
              position: 'fixed',
              left: Math.min(gearMenu.x, window.innerWidth - 200),
              top: Math.min(gearMenu.y, window.innerHeight - 100),
              backgroundColor: '#fff',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '1px solid #e5e7eb',
              padding: '4px 0',
              minWidth: '180px',
              zIndex: 1001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gear-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#374151',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={() => {
                const prod = gearMenu.product
                setGearMenu(null)
                openEditModal(prod)
              }}
            >
              <PencilIcon size={13} />
              <span>Düzenle</span>
            </button>
            <button
              type="button"
              className="gear-menu-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#374151',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              onClick={async () => {
                const prod = gearMenu.product
                setGearMenu(null)
                try {
                  const demoRes = await api.copyProductToDemo(prod.id)
                  alert(`"${prod.name}" başarıyla demo ortama kopyalandı. "Demo Ürünler" sekmesinden inceleyebilirsiniz.`)
                  if (setDemoProducts) {
                    setDemoProducts((prev) => [demoRes, ...prev])
                  } else {
                    await onRefresh()
                  }
                } catch (err: any) {
                  alert('Kopyalama sırasında hata oluştu: ' + (err.message || 'Bilinmeyen hata'))
                }
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <span>Demo Ortamına Kopyala</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
